import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { UserListItem, UserRole } from "../Dto/UserModel";
import { ManageResetPasswordRequest } from "../Dto/UserManageResetPasswordRequest";
import { ResetPasswordResponse } from "../../Auth/Dto/ResetPasswordResponse";
import { SearchUserRequest } from "../Dto/UserSearchRequest";
import { SearchUserResponse } from "../Dto/UserSearchResponse";

@Injectable({ providedIn: "root" })
export class UserApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchUsers(params: SearchUserRequest): Promise<SearchUserResponse> {
    const url = new URL("users", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) {
      searchParams.set("search", params.search);
    }

    if (params.email) {
      searchParams.set("email", params.email);
    }

    if (params.name) {
      searchParams.set("name", params.name);
    }

    if (params.role) {
      searchParams.set("role", params.role);
    }

    if (params.createdAtFrom) {
      searchParams.set("createdAtFrom", params.createdAtFrom);
    }

    if (params.createdAtTo) {
      searchParams.set("createdAtTo", params.createdAtTo);
    }

    if (params.page !== undefined) {
      searchParams.set("page", params.page.toString());
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    return await firstValueFrom(
      this.http.get<import("../Dto/UserSearchResponse").SearchUserResponse>(url.toString(), { withCredentials: true }),
    );
  }

  async createUser(payload: import("../Dto/UserCreateRequest").CreateUserRequest): Promise<UserListItem> {
    const url = new URL("users/manage", `${this.baseUrl}/`);
    const createData: {
      name: string;
      email: string;
      password: string;
      role?: string;
      phone?: string;
      address?: string;
      imageUrl?: string;
    } = {
      name: payload.name,
      email: payload.email,
      password: payload.password,
    };

    if (payload.role !== undefined) createData.role = payload.role;
    if (payload.phone !== undefined) createData.phone = payload.phone;
    if (payload.address !== undefined) createData.address = payload.address;
    if (payload.imageUrl != undefined) createData.imageUrl = payload.imageUrl;

    return await firstValueFrom(this.http.post<UserListItem>(url.toString(), createData, { withCredentials: true }));
  }

  async updateUser(userId: string, userData: Partial<UserListItem>): Promise<UserListItem> {
    const url = new URL(`users/${userId}`, `${this.baseUrl}/`);
    // Create update payload with proper field mapping
    const updateData: {
      name?: string;
      email?: string;
      phone?: string;
      address?: string;
      role?: string | UserRole;
    } = {};

    if (userData.name !== undefined) updateData.name = userData.name;
    if (userData.email !== undefined) updateData.email = userData.email;
    if (userData.phone != undefined) updateData.phone = userData.phone;
    if (userData.address != undefined) updateData.address = userData.address;
    if (userData.role != undefined) {
      updateData.role = (() => {
        if (typeof userData.role === "object" && userData.role?.id) {
          return userData.role.id;
        }
        return userData.role;
      })();
    }

    return await firstValueFrom(this.http.put<UserListItem>(url.toString(), updateData, { withCredentials: true }));
  }

  async updateProfile(userId: string, profileData: Partial<UserListItem>): Promise<void> {
    const url = new URL(`users/profile/${userId}`, `${this.baseUrl}/`);
    // Create profile update payload with proper field mapping
    const updateData: {
      name?: string;
      email?: string;
      phone?: string;
      address?: string;
      imageUrl?: string;
      role?: string | UserRole;
    } = {};

    if (profileData.name !== undefined) updateData.name = profileData.name;
    if (profileData.email !== undefined) updateData.email = profileData.email;
    if (profileData.phone != undefined) updateData.phone = profileData.phone;
    if (profileData.address != undefined) updateData.address = profileData.address;
    if (profileData.imageUrl != undefined) updateData.imageUrl = profileData.imageUrl;

    await firstValueFrom(this.http.put<void>(url.toString(), updateData, { withCredentials: true }));
  }

  async updateManageProfile(userId: string, profileData: Partial<UserListItem>): Promise<UserListItem> {
    const url = new URL(`users/manage/profile/${userId}`, `${this.baseUrl}/`);
    const updateData: {
      name?: string;
      email?: string;
      phone?: string;
      address?: string;
      imageUrl?: string;
      role?: string | UserRole;
    } = {};

    if (profileData.name !== undefined) updateData.name = profileData.name;
    if (profileData.email !== undefined) updateData.email = profileData.email;
    if (profileData.phone != undefined) updateData.phone = profileData.phone;
    if (profileData.address != undefined) updateData.address = profileData.address;
    if (profileData.imageUrl != undefined) updateData.imageUrl = profileData.imageUrl;
    if (profileData.role != undefined) {
      updateData.role = (() => {
        if (typeof profileData.role === "object" && profileData.role?.id) {
          return profileData.role.id;
        }
        return profileData.role;
      })();
    }

    return await firstValueFrom(this.http.put<UserListItem>(url.toString(), updateData, { withCredentials: true }));
  }

  async getManageProfile(userId: string): Promise<UserListItem> {
    const url = new URL(`users/manage/profile/${userId}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<UserListItem>(url.toString(), { withCredentials: true }));
  }

  async resetPasswordByAdmin(userId: string, payload: ManageResetPasswordRequest): Promise<ResetPasswordResponse> {
    const url = new URL(`users/manage/reset-password/${userId}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<ResetPasswordResponse>(url.toString(), payload, { withCredentials: true }));
  }

  async deleteImage(): Promise<void> {
    const url = new URL("users/image", `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async deleteManageUser(userId: string): Promise<void> {
    const url = new URL(`users/manage/${userId}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async getProfile(userId: string): Promise<UserListItem> {
    const url = new URL(`users/profile/${userId}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<UserListItem>(url.toString(), { withCredentials: true }));
  }
}
